import { useQuery } from '@tanstack/react-query'
import { ApiError, analyticsFetch, getCallerAgentId, hasLiveAnalytics } from '@/api/client'
import { isUnreachableApiError } from '@/api/unreachable'
import {
  fromPnlFixture,
  isUnavailable,
  toCommissionReport,
  unavailableCommissionReport,
  type AnalyticsCommissionResponse,
  type CommissionReport,
} from '@/api/map/commission'
import type { AnalyticsDailyResponse } from '@/api/map/pnl'
import * as api from '@/data/api'
import { keys } from '@/api/hooks/shared/keys'

function rangeQuery(range: { from: string; to: string }): string {
  return new URLSearchParams({
    from: range.from.slice(0, 10),
    to: range.to.slice(0, 10),
  }).toString()
}

async function loadCommission(query: api.PnlQuery): Promise<CommissionReport> {
  const { range } = query
  if (hasLiveAnalytics()) {
    // Scope is honoured live because the route is per-agent; sport, game and
    // member are not in the commission contract, so the filter bar narrows them
    // client-side rather than the page claiming the server did it.
    const id = query.rootId ?? getCallerAgentId()
    if (!id) {
      return unavailableCommissionReport({
        code: 'mart_unavailable',
        reason: 'No agent id on the session — commission cannot be loaded.',
      })
    }
    const qs = rangeQuery(range)
    const dailyPromise = analyticsFetch<AnalyticsDailyResponse>(
      `/v1/agents/${id}/daily?${qs}`,
    ).catch(() => ({ rows: [] as AnalyticsDailyResponse['rows'] }))

    try {
      const live = await analyticsFetch<AnalyticsCommissionResponse>(
        `/v1/agents/${id}/commission?${qs}`,
      )
      const daily = await dailyPromise
      return toCommissionReport(live, daily)
    } catch (err) {
      if (err instanceof ApiError && err.status === 503 && isUnavailable(err.body)) {
        const daily = await dailyPromise
        return unavailableCommissionReport(err.body, daily)
      }
      if (isUnreachableApiError(err)) {
        const daily = await dailyPromise
        return unavailableCommissionReport(
          {
            code: 'mart_unavailable',
            reason: 'Commission could not be reached. No data found until the service responds.',
          },
          daily,
        )
      }
      throw err
    }
  }

  return fromPnlFixture(await api.getPnl({ ...query, page: 1, pageSize: 100_000 }))
}

export function useCommission(query: api.PnlQuery) {
  return useQuery({
    queryKey: keys.commission(query),
    queryFn: () => loadCommission(query),
    placeholderData: (previous) => previous,
  })
}
